import { useState } from "react";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import { Tag } from "antd";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import trans, { isRTL } from "../../../utils";
import BreadCrumb from "../../Header/SecondHeader";
import AntdTable from "../../antd/Table/Table";
import AccessDenied from "../../AccessDenied";
import usePermission from "../../../hooks/usePermission";
import { formatDateString } from "../../../JsHelpers/DateFormat";
import { useInfiniteTechnicianOptions } from "../../../hooks/useInfiniteTechnicianOptions";
import InfiniteSelectFilterDropdown from "../../Tasks/List/InfiniteSelectFilterDropdown";
import { useGetAllDispatchPlanQuery } from "../../../Redux/service/Dispatcher/DispatcherPlan";
import DispatcherPlansHead from "./DispatcherPlansHead";
import NewDispatchPlanModal from "./NewDispatchPlanModal";
import DispatcherPlanActions from "./DispatcherPlanActions";
import ViewDispatchPlanModal from "./ViewDispatchPlanModal";
import CancelDispatchPlanModal from "./CancelDispatchPlanModal";
import ReassignDispatchPlanModal from "./ReassignDispatchPlanModal";
import EditDispatchPlanModal from "./EditDispatchPlanModal";
import ActivateDispatchPlanModal from "./ActivateDispatchPlanModal";

const PLAN_STATUS_COLORS = {
	Planned: "gold",
	Pending: "gold",
	Active: "blue",
	Completed: "green",
	Cancelled: "red",
};

const PLAN_STATUS_OPTIONS = [
	{ text: trans("Planned"), value: "Planned" },
	{ text: trans("Active"), value: "Active" },
	{ text: trans("Completed"), value: "Completed" },
	{ text: trans("Cancelled"), value: "Cancelled" },
];

const SORT_FIELD_BY_COLUMN_KEY = {
	name: "name",
	plannedDate: "plannedDate",
	createdAt: "createdAt",
	updatedAt: "lastUpdate",
	lastUpdate: "lastUpdate",
	assignedTo: "assignedTo",
};

function arraysEqual(a = [], b = []) {
	if (a.length !== b.length) return false;
	const sortedA = [...a].sort();
	const sortedB = [...b].sort();
	return sortedA.every((v, i) => v === sortedB[i]);
}

export default function DispatcherPlans() {
	const { view, create, edit } = usePermission("DISPATCH_PLAN");
	const auth = useSelector((state) => state.users.auth);
	const profile = useSelector((state) => state.users.user);
	const isAdmin = auth?.administrator || profile?.administrator;
	const canView = view || isAdmin;
	const canCreate = create || isAdmin;
	const canEdit = edit || isAdmin;
	const [searchParams, setSearchParams] = useSearchParams();
	const [createPlan, setCreatePlan] = useState(false);
	const [viewPlanId, setViewPlanId] = useState(null);
	const [cancelPlan, setCancelPlan] = useState(null);
	const [reassignPlan, setReassignPlan] = useState(null);
	const [editPlanId, setEditPlanId] = useState(null);
	const [activatePlanId, setActivatePlanId] = useState(null);
	const [search, setSearch] = useState(searchParams.get("search") || "");
	const [status, setStatus] = useState(
		searchParams.get("status") ? searchParams.get("status").split(",") : [],
	);
	const [assignedTo, setAssignedTo] = useState(
		searchParams.get("assignedTo")
			? searchParams.get("assignedTo").split(",")
			: [],
	);
	const [currentPage, setCurrentPage] = useState(
		Number(searchParams.get("page")) || 1,
	);
	const [limit, setLimit] = useState(Number(searchParams.get("limit")) || 25);
	const [sort, setSort] = useState({
		sortBy: searchParams.get("sortBy") || "createdAt",
		sortOrder: searchParams.get("sortOrder") || "desc",
	});
	const [activeFilterKey, setActiveFilterKey] = useState(null);

	const { data, isFetching } = useGetAllDispatchPlanQuery({
		search: search || undefined,
		status: status.length ? status.join(",") : undefined,
		assignedTo: assignedTo.length ? assignedTo.join(",") : undefined,
		page: currentPage,
		limit,
		sortBy: sort.sortBy,
		sortOrder: sort.sortOrder,
	});
	const plans = data?.data || [];

	const syncUrl = (
		nextSearch,
		nextStatus,
		nextAssignedTo,
		nextPage,
		nextLimit,
		nextSort,
	) => {
		const params = new URLSearchParams();
		if (nextSearch) params.set("search", nextSearch);
		if (nextStatus.length) params.set("status", nextStatus.join(","));
		if (nextAssignedTo.length)
			params.set("assignedTo", nextAssignedTo.join(","));
		params.set("page", nextPage);
		params.set("limit", nextLimit);
		params.set("sortBy", nextSort.sortBy);
		params.set("sortOrder", nextSort.sortOrder);
		setSearchParams(params, { replace: true });
	};

	const handleSearchChange = (value) => {
		setSearch(value);
		setCurrentPage(1);
		syncUrl(value, status, assignedTo, 1, limit, sort);
	};

	const handlePageChange = (page) => {
		setCurrentPage(page);
		syncUrl(search, status, assignedTo, page, limit, sort);
	};

	const handleLimitChange = (newLimit) => {
		setLimit(newLimit);
		setCurrentPage(1);
		syncUrl(search, status, assignedTo, 1, newLimit, sort);
	};

	const handleTableChange = (pagination, tableFilters, sorter) => {
		const nextSort = sorter.order
			? {
					sortBy: SORT_FIELD_BY_COLUMN_KEY[sorter.field] || "createdAt",
					sortOrder: sorter.order === "ascend" ? "asc" : "desc",
				}
			: { sortBy: "createdAt", sortOrder: "desc" };
		const sortChanged =
			nextSort.sortBy !== sort.sortBy || nextSort.sortOrder !== sort.sortOrder;
		setSort(nextSort);

		const nextStatus =
			activeFilterKey === "status" ? tableFilters?.status || [] : status;
		const nextAssignedTo =
			activeFilterKey === "assignedTo"
				? tableFilters?.assignedTo || []
				: assignedTo;
		const filtersChanged =
			!arraysEqual(nextStatus, status) || !arraysEqual(nextAssignedTo, assignedTo);
		setStatus(nextStatus);
		setAssignedTo(nextAssignedTo);

		const targetPage =
			filtersChanged || sortChanged ? 1 : pagination?.current || currentPage;
		setCurrentPage(targetPage);
		syncUrl(search, nextStatus, nextAssignedTo, targetPage, limit, nextSort);
	};

	const notImplemented = (message) => () =>
		toast.info(trans(message), { autoClose: 2500 });

	const columns = [
		{
			title: trans("Plan Name"),
			dataIndex: "name",
			key: "name",
			sorter: true,
			fixed: true,
			width: 180,
			render: (name, record) => (
				<div>
					<div className="fw-500">{record.code}</div>
					<div className="neutral-400">{name}</div>
				</div>
			),
		},
		{
			title: trans("Planned Date"),
			dataIndex: "plannedDate",
			key: "plannedDate",
			sorter: true,
			width: 150,
			render: (v) => (v ? formatDateString(v) : "-"),
		},
		{
			title: trans("Tasks Number"),
			dataIndex: "taskCount",
			key: "taskCount",
			width: 120,
		},
		{
			title: trans("Assigned Staff"),
			key: "assignedTo",
			sorter: true,
			width: 160,
			filteredValue: assignedTo.length ? assignedTo : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("assignedTo");
			},
			filterDropdown: (dropdownProps) => (
				<InfiniteSelectFilterDropdown
					{...dropdownProps}
					useOptions={useInfiniteTechnicianOptions}
					searchPlaceholder={trans("Search field staff")}
				/>
			),
			render: (_, record) => (
				<div className="text-capitalize">
					{record.assignedTo?.name || trans("Unassigned")}
				</div>
			),
		},
		{
			title: trans("Status"),
			dataIndex: "status",
			key: "status",
			width: 130,
			filters: PLAN_STATUS_OPTIONS,
			filterSearch: true,
			filterMultiple: true,
			filteredValue: status.length ? status : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("status");
			},
			render: (planStatus) => (
				<Tag
					color={PLAN_STATUS_COLORS[planStatus] || "default"}
					className="text-capitalize"
				>
					{trans(planStatus)}
				</Tag>
			),
		},
		{
			title: trans("Created At"),
			dataIndex: "createdAt",
			key: "createdAt",
			sorter: true,
			width: 180,
			render: (v) => formatDateString(v),
		},
		{
			title: trans("Last Updated"),
			dataIndex: "updatedAt",
			key: "lastUpdate",
			sorter: true,
			width: 180,
			render: (v) => formatDateString(v),
		},
		{
			title: trans("Actions"),
			key: "actions",
			width: 140,
			fixed: !isRTL ? "right" : undefined,
			render: (_, record) => (
				<DispatcherPlanActions
					plan={record}
					canEdit={canEdit}
					onView={() => setViewPlanId(record.id)}
					onEdit={() => setEditPlanId(record.id)}
					onActivate={() => setActivatePlanId(record.id)}
					onReassign={(p) => setReassignPlan(p)}
					onCancel={(p) => setCancelPlan(p)}
				/>
			),
		},
	];

	if (!canView) {
		return <AccessDenied />;
	}

	return (
		<div>
			<Helmet>
				<title>Dispatcher Plans</title>
				<meta name="description" content="DispatcherPlans" />
			</Helmet>
			<BreadCrumb
				title={trans("Dispatcher Plan")}
				add={canCreate && trans("Create new Plan")}
				onClick={() => {
					if (canCreate) {
						setCreatePlan(true);
					} else {
						return null;
					}
				}}
			/>
			<DispatcherPlansHead value={search} onChange={handleSearchChange} />

			<div className="bg-white-rounded-top mt-3 table-parent">
				<AntdTable
					columns={columns}
					loading={isFetching}
					dataSource={plans}
					pageSize={limit}
					totalItems={data?.pagination?.totalItems || 0}
					currentPage={currentPage}
					onPageChange={handlePageChange}
					onLimitChange={handleLimitChange}
					onTableChange={handleTableChange}
					scrollX={true}
				/>
			</div>

			<NewDispatchPlanModal
				open={createPlan}
				onClose={() => setCreatePlan(false)}
			/>
			<ViewDispatchPlanModal
				planId={viewPlanId}
				onClose={() => setViewPlanId(null)}
				onRemoveRequest={notImplemented(
					"Removing a request from a plan is coming soon",
				)}
			/>
			<CancelDispatchPlanModal
				plan={cancelPlan}
				onClose={() => setCancelPlan(null)}
			/>
			<ReassignDispatchPlanModal
				plan={reassignPlan}
				onClose={() => setReassignPlan(null)}
			/>
			<EditDispatchPlanModal
				planId={editPlanId}
				onClose={() => setEditPlanId(null)}
			/>
			<ActivateDispatchPlanModal
				planId={activatePlanId}
				onClose={() => setActivatePlanId(null)}
			/>
		</div>
	);
}
